<template>
  <no-ssr>
    <popup v-model="visible" size="sm" class="popup-individual">
      <transition name="fade" mode="out-in">
        <div
          v-if="pending"
          :key="'preloader'"
          class="popup-individual__preloader"
        >
          <div class="popup-individual__preloader-container">
            <preloader visible />
          </div>
        </div>
        <div
          v-else-if="done"
          :key="'done'"
          class="popup-individual__done u-text-center"
        >
          <div class="popup-individual__done-icon">
            <svg-inline>
              <icon icon="checkmark-circle" />
            </svg-inline>
          </div>
          <div class="font-h1" v-html="$t('popupIndividual.done.title')"></div>
          <div
            class="popup-individual__text font-semibold"
            v-html="$t('popupIndividual.done.text')"
          ></div>
          <Button
            class="popup-individual__link"
            tag="a"
            href="/lk/mytours/bookings/"
            color="gray"
            :text="$t('popupIndividual.done.link')"
          />
        </div>
        <div
          v-else-if="!done"
          class="popup-individual__container u-text-center"
        >
          <form class="popup-individual__form" @submit.prevent="onSubmit">
            <div
              class="font-h1 popup-indiv__uniq__class"
              v-html="$t('popupIndividual.title')"
            ></div>
            <div
              class="popup-individual__text font-semibold"
              v-html="$t('popupIndividual.text')"
            ></div>
            <booking-popup-fieldset
              :range.sync="$v.formData.range.$model"
              :participants.sync="$v.formData.participants.$model"
              :is-custom-offer="true"
              :pending="pending"
              :error="$v.formData.range.$invalid"
              class="booking-popup__fieldset"
            />
            <text-area
              :value="$v.formData.message.$model"
              :placeholder="$t('popupIndividual.message')"
              @field-input="$v.formData.message.$model = $event"
            >
              <template #error-helper>
                <div
                  v-if="backendErrorMessage"
                  v-html="backendErrorMessage"
                ></div>
              </template>
            </text-area>
            <div class="popup-individual__price">
              <div
                class="font-semibold"
                v-html="this.minDays > 1 ? $t('popupIndividual.priceMoreDay') : $t('popupIndividual.price')"
              ></div>
              <div class="popup-individual__price-value font-h0">
                {{ symbol }} {{ priceDayFormatted }}
              </div>
            </div>
            <div class="u-text-secondary">
              <div
                class="font-text-small"
                v-html="$t('bookingPopup.note.cancel')"
              ></div>
            </div>
            <Button
              class="popup-individual__submit"
              tag="button"
              type="submit"
              :disabled="pending || $v.formData.$error"
            >
              {{ $t("popupIndividual.submit") }}
            </Button>
          </form>
        </div>
      </transition>
    </popup>
  </no-ssr>
</template>

<script>
import { mapGetters } from "vuex";
import Popup from "@/components/ui-kit/popup/index.vue";
import BookingPopupFieldset from "@/components/organisms/booking-popup/fieldset.vue";
import TextArea from "@/components/atoms/field-area/index.vue";
import Button from "@/components/ui-kit/button/index.vue";
import Preloader from "@/components/ui-kit/preloader/index.vue";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";
import noSSR from "vue-no-ssr";
import { differenceInDays, parse } from "date-fns";
import { minValue, required } from "vuelidate/lib/validators";
import { axiosOrders } from "@/services/scopes/axios-orders";
import getBackendErrorMessage from "@/assets/js/utils/get-backend-error-message";
import getVuelidateErrorMessage from "@/assets/js/utils/get-vuelidate-error-message";
import thousandsSeparator from "@/assets/js/utils/thousandsSeparator";

export default {
  components: {
    BookingPopupFieldset,
    Popup,
    TextArea,
    Button,
    Preloader,
    SvgInline,
    Icon,
    "no-ssr": noSSR,
  },
  model: {
    prop: "modelValue",
    event: "change",
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    tour: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        range: [],
        participants: 1,
        message: "",
      },
      pending: false,
      backendErrorMessage: "",
      done: false,
    };
  },
  validations() {
    return {
      formData: {
        range: {
          required,
        },
        participants: {
          required,
          minValue: minValue(1),
        },
        message: {},
      },
    };
  },
  computed: {
    ...mapGetters("currency", ["symbol"]),
    ...mapGetters("user", ["getUserLoginStatus"]),
    minDays() {
      return this.formData.range.length
        ? Math.abs(
            differenceInDays(
              parse(this.formData.range[0], "dd.MM.yyyy", new Date()),
              parse(this.formData.range[1], "dd.MM.yyyy", new Date())
            )
          ) + 2
        : 1;
    },
    countParticipants() {
      return this.formData.participants;
    },
    priceDay() {
      return (
        ((+this.tour.private_price_day || +this.tour.min_price) * this.minDays) * this.countParticipants
      );
    },
    priceDayFormatted() {
      return thousandsSeparator(this.priceDay);
    },
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("change", value);
      },
    },
  },
  watch: {
    visible(visible) {
      if (!visible && this.done) {
        this.pending = false;
        this.done = false;

        this.formData.offer = {};
        this.formData.participants = 1;
        this.formData.message = "";
        this.offer = {};
        this.offers = [];
        this.$v.formData.$reset();
      }
    },
    formData: {
      deep: true,
      handler() {
        this.backendErrorMessage = "";
      },
    },
  },
  mounted() {
    fakeWindow.addEventListener("auth.close", this.onAuthClose);
    fakeWindow.addEventListener("auth.success", this.onAuthSuccess);
  },
  beforeDestroy() {
    fakeWindow.removeEventListener("auth.close", this.onAuthClose);
    fakeWindow.removeEventListener("auth.success", this.onAuthSuccess);
  },
  methods: {
    async onSubmit() {
      this.$v.formData.$touch();

      if (this.pending || this.$v.formData.$invalid) {
        return;
      }

      this.pending = true;

      if (this.getUserLoginStatus) {
        return this.book();
      }

      this.visible = false;
      setTimeout(() =>
        fakeWindow.showVueAuthModal({ "emit-event": "popup-individual" })
      );
    },
    async book() {
      this.pending = true;

      const {
        tour: { id: tourId },
        formData: { participants: peopleCounter, range, message },
      } = this;

      await axiosOrders
        .orderAdd("customDates", {
          tourId,
          peopleCounter,
          date: 1,
          customDatesFrom: range[0],
          customDatesTo: range[1],
          message,
          type: "customDates",
        })
        .then(() => {
          this.done = true;
          this.pending = false;
        })
        .catch((error) => {
          this.backendErrorMessage = this.getBackendErrorMessage(error);
          this.pending = false;
        });
    },
    onAuthClose({ detail }) {
      if (detail === "popup-individual") {
        this.visible = true;
        this.pending = false;
      }
    },
    onAuthSuccess({ detail }) {
      if (detail === "popup-individual") {
        setTimeout(() => {
          this.onSubmit();
          this.visible = true;
        });
      }
    },
    getBackendErrorMessage,
    getVuelidateErrorMessage,
  },
};
</script>

<style lang="scss" scoped>
.popup-individual {
  &__form,
  &__done {
    > * + * {
      margin-top: 24px;
    }
  }

  &__text {
    font-weight: 400;
    padding: 0 1%;
  }

  &__price {
    padding: 16px 20px;
    background: var.$gray-light;
    border-radius: 16px;

    &-value {
      margin-top: 4px;
    }
  }

  &__submit,
  &__link {
    width: 100%;
  }

  &__done {
    &-icon {
      max-width: 64px;
      margin-right: auto;
      margin-left: auto;
    }
  }
}

@media screen and (max-width: 390px) {
  .popup-indiv__uniq__class {
    max-width: 245px;
    text-align: center;
    margin: 0 auto;
  }
}
</style>
